import { isValidElement, type ReactNode } from 'react';
import T5IconButton from 'threads5/IconButton';

// Arrow nav button for the web3 Link Carousel (SITE-6129). Scoped to the
// carousel so it doesn't affect other IconButton usages (e.g. the homepage
// carousel, which stays on its existing treatment).

const BUTTON_SIZE = { xs: '48px', lg: '54px' };
// Arrow glyph — sized to the updated Figma component (18px frame in the 54px
// circle, node 15030-23148; the pre-update component was larger, hence the
// earlier spec discrepancy).
const ICON_SIZE = { xs: '16px', lg: '18px' };
// Clip box for the sliding arrow — one "slot"; the slide distance equals this.
const SLOT_SIZE = { xs: '20px', lg: '22px' };
const BASE_TRANSITION = 'all 0.1s ease-in-out';
const MASK = 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)';

// Arrow slide: a standard ease-out at the prototype's pace (~0.46s, no
// overshoot — ~90% by ~240ms). cubic-bezier(0.33, 1, 0.68, 1) is easeOutCubic.
const SLIDE_TRANSITION = 'transform 0.46s cubic-bezier(0.33, 1, 0.68, 1)';
// Scale is decoupled from the slide: a snappy ease so the hover registers
// immediately (the prototype's 0.5s scale spring read as laggy). Magnitude
// stays 1.04. Snaps back even quicker on mouse-out.
const SCALE_TRANSITION = 'transform 0.2s ease-out';
const SCALE_RETURN = 'transform 0.13s ease-out';

const ArrowSlider = ({ children }: { children: ReactNode }) => {
  return (
    <span className='arrow-slider' aria-hidden>
      <span className='arrow-slot arrow-primary'>{children}</span>
      <span className='arrow-slot arrow-secondary'>{children}</span>
    </span>
  );
};

const CarouselArrowButton = ({
  children,
  icon,
  slideDirection,
  disabled,
  appearance = 'outline',
  ...rest
}: {
  children?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
  // The arrow loops in its pointing direction on hover (carousel nav).
  slideDirection: 'left' | 'right';
  disabled?: boolean;
  // "outline" gives a transparent centre (no fill behind the arrow); the
  // ring/fill treatment is drawn on ::before. Its own border is suppressed in
  // the sx below.
  appearance?: 'solid' | 'outline' | 'ghost';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}) => {
  const isLeft = slideDirection === 'left';
  const exit = isLeft ? '-100%' : '100%';
  const enter = isLeft ? '100%' : '-100%';

  const renderIcon = (iconProps) => {
    const resolved = typeof icon === 'function' ? icon(iconProps) : icon;
    return <ArrowSlider>{resolved}</ArrowSlider>;
  };

  return (
    <T5IconButton
      appearance={appearance}
      disabled={disabled}
      icon={
        isValidElement(icon) || typeof icon === 'function' ? renderIcon : icon
      }
      sx={(theme) => {
        const isDark = theme.palette.mode === 'dark';
        // Carousel-scoped colours, inlined here rather than read from the shared
        // web3/component/icon-button/* theme tokens — those are also consumed by
        // the homepage IconButton, which must keep its existing treatment.
        // Active = enabled (light elizabeth fill / dark benjamin stroke);
        // inactive = disabled (light elizabeth stroke / dark sky-700 stroke).
        // Hover adds only motion, not a colour change — matching the prototype.
        const enabledBg = isDark
          ? 'linear-gradient(to right, #42F0CD 0%, #0DADF2 100%)' // benjamin
          : theme.palette['gradient/background/stark/elizabeth'];
        const disabledBg = isDark
          ? theme.palette['base/sky/700']
          : theme.palette['gradient/background/stark/elizabeth'];
        const svgFill = isDark
          ? theme.palette['base/white']
          : theme.palette['base/sky/900'];

        // The circle treatment is drawn on ::before so the arrow stays solid on
        // top and (light mode) the fill/stroke can `multiply` against any page
        // background. Light multiplies; dark does not.
        const base = {
          content: '""',
          position: 'absolute' as const,
          inset: 0,
          borderRadius: '100%',
          pointerEvents: 'none' as const,
          transition: BASE_TRANSITION,
          ...(isDark ? {} : { mixBlendMode: 'multiply' as const }),
        };
        // Solid stroke as a crisp CSS border — the faithful render of Figma's
        // 2px INSIDE stroke (no mask anti-alias bloom).
        const borderStroke = (color: string) => {
          return {
            ...base,
            border: `2px solid ${color}`,
            boxSizing: 'border-box' as const,
          };
        };
        // Gradient stroke (transparent center) via a masked border — used where
        // the stroke is a gradient (rounded gradient borders need this).
        const maskStroke = (background: string) => {
          return {
            ...base,
            padding: '2px',
            background,
            mask: MASK,
            maskComposite: 'exclude',
            WebkitMask: MASK,
            WebkitMaskComposite: 'xor',
          };
        };
        // Solid filled circle.
        const fill = (background: string) => {
          return { ...base, background };
        };

        // Enabled (rest + hover): light fills, dark = benjamin gradient stroke.
        const enabledBefore = isDark ? maskStroke(enabledBg) : fill(enabledBg);
        // Disabled: light = elizabeth gradient stroke, dark = solid sky-700.
        const disabledBefore = isDark
          ? borderStroke(disabledBg)
          : maskStroke(disabledBg);

        return {
          position: 'relative',
          borderRadius: '100% !important',
          width: BUTTON_SIZE,
          height: BUTTON_SIZE,
          // threads5's outline appearance adds its own border and a faint hover
          // tint; the ::before treatment is the only treatment we want, so keep
          // the root transparent and borderless in every state. Doubling the
          // ampersand out-specifies threads5's :hover / :active / [aria-disabled]
          // rules, so no !important is needed.
          '&&, &&:hover, &&:active, &&.Mui-disabled': {
            border: 'none',
            background: 'transparent',
          },
          // Quick scale-back on mouse-out (the spring plays on hover-in below).
          // No rest transform is set, so the light `multiply` still composites
          // against the page until the button is hovered (a transform would
          // create a stacking context and isolate the blend).
          transition: SCALE_RETURN,
          // Single ::before declaration per state — switching disabled on/off
          // must fully replace the treatment, not layer a border over the
          // enabled fill/mask.
          '&::before': disabled ? disabledBefore : enabledBefore,
          svg: {
            width: ICON_SIZE,
            height: ICON_SIZE,
            transition: BASE_TRANSITION,
            path: {
              fill: svgFill,
              transition: BASE_TRANSITION,
            },
          },
          i: {
            width: ICON_SIZE,
            height: ICON_SIZE,
          },
          '.arrow-slider': {
            position: 'relative',
            display: 'inline-flex',
            width: SLOT_SIZE,
            height: SLOT_SIZE,
            overflow: 'clip',
          },
          '.arrow-slot': {
            position: 'absolute',
            inset: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: SLIDE_TRANSITION,
          },
          '.arrow-secondary': {
            transform: `translateX(${enter})`,
          },
          '@media (hover: hover)': {
            ':hover:not(:disabled)': {
              // Subtle 1.04 scale + arrow loop-through. No colour change, no
              // glow, no shadow — per Figma + the Framer prototype.
              transition: SCALE_TRANSITION,
              transform: 'scale(1.04)',
              '.arrow-primary': { transform: `translateX(${exit})` },
              '.arrow-secondary': { transform: 'translateX(0)' },
            },
            ':active:not(:disabled)': {
              transform: 'scale(0.96)',
            },
          },
          // Reduced motion: no scale, no slide (state changes are instant).
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&::before': { transition: 'none' },
            svg: { transition: 'none', path: { transition: 'none' } },
            '.arrow-slot': { transition: 'none' },
            ':hover:not(:disabled)': {
              transform: 'none',
              '.arrow-primary': { transform: 'translateX(0)' },
              '.arrow-secondary': { transform: `translateX(${enter})` },
            },
            ':active:not(:disabled)': { transform: 'none' },
          },
        };
      }}
      {...rest}
    >
      {children}
    </T5IconButton>
  );
};

export default CarouselArrowButton;
